// Una sola pestaña «líder» para los avisos de la Sala (Sala CRM↔HCE, rebanada 5; PLAN 4.11 y 9-C6): con varias pestañas del HCE abiertas, solo una
// muestra el aviso «Paciente X llegó».
//
// Por qué un bloqueo PROPIO y no el `esLider` de `useEventosSala`: ese bloqueo se elige por INSTANCIA del hook, y en una misma pestaña hay varias (el
// aviso global, el Dashboard, la Sala…). Quien lo gana depende de en qué orden se montaron, así que el aviso global podía no ser el líder aunque su
// pestaña lo fuera, y entonces nadie avisaba. Este bloqueo lo pide UNA sola instancia por pestaña (el aviso global): el navegador lo concede a una
// sola pestaña a la vez y lo traspasa solo si esa pestaña se cierra.
//
// Sin Web Locks (navegador viejo) no hay forma segura de elegir: cada pestaña se considera líder (puede haber un aviso por pestaña, nunca ninguno).
import { useEffect, useState } from 'react';
import type { AdministradorBloqueos } from './eventosSala';

export function crearLiderUnico(o: { clave: string; bloqueos: AdministradorBloqueos | null; alCambio: (esLider: boolean) => void }): { iniciar: () => void; detener: () => void } {
  let activa = false;
  let liberar: (() => void) | null = null;
  let abortar: AbortController | null = null;

  return {
    iniciar() {
      if (activa) return;
      activa = true;
      if (!o.bloqueos) {
        o.alCambio(true);
        return;
      }
      abortar = new AbortController();
      // La petición espera hasta que esta pestaña sea la líder; el navegador la suelta solo al cerrarse la pestaña, o al detener.
      o.bloqueos
        .request(o.clave, { signal: abortar.signal }, () => new Promise<void>((resolver) => {
          // Si se detuvo entre la concesión y este callback, no hay a quién liderar: se suelta ya (si no, el bloqueo quedaría retenido y ninguna otra
          // pestaña llegaría a ser líder).
          if (!activa) {
            resolver();
            return;
          }
          liberar = resolver;
          o.alCambio(true);
        }))
        .catch(() => {
          // AbortError al detener antes de ser líder: nada que limpiar
        });
    },
    detener() {
      if (!activa) return;
      activa = false;
      abortar?.abort();
      abortar = null;
      liberar?.();
      liberar = null;
      o.alCambio(false);
    },
  };
}

/**
 * Mantiene el liderazgo SOLO mientras la pestaña está visible: una pestaña oculta suelta el bloqueo (y no hace cola), así otra pestaña visible puede
 * avisar. Sin esto, una líder escondida detrás de otras se quedaba con el aviso y el médico no veía nada. Si ninguna pestaña está visible no avisa
 * nadie; al volver a verla, la pestaña recupera el liderazgo y avisa de las llegadas pendientes (ver AvisosSala).
 */
export type DocumentoVisible = {
  visibilityState: string;
  addEventListener: (tipo: 'visibilitychange', fn: () => void) => void;
  removeEventListener: (tipo: 'visibilitychange', fn: () => void) => void;
};

export function seguirVisibilidad(lider: { iniciar: () => void; detener: () => void }, doc: DocumentoVisible): () => void {
  const sincronizar = () => (doc.visibilityState === 'hidden' ? lider.detener() : lider.iniciar());
  doc.addEventListener('visibilitychange', sincronizar);
  sincronizar();
  return () => {
    doc.removeEventListener('visibilitychange', sincronizar);
    lider.detener();
  };
}

/** Clave del bloqueo del aviso «Paciente X llegó». Cada aviso con su propia líder usa su propia clave: dos solicitudes de la MISMA clave en una pestaña se harían cola entre sí. */
export const CLAVE_LIDER_LLEGADAS = 'lc-sala-avisos';

export function bloqueosDelNavegador(): AdministradorBloqueos | null {
  return typeof navigator !== 'undefined' ? ((navigator as unknown as { locks?: AdministradorBloqueos }).locks ?? null) : null;
}

/** `true` mientras ESTA pestaña es la líder de los avisos de la Sala (y está visible). */
export function useLiderDeAvisos(activo: boolean, clave: string = CLAVE_LIDER_LLEGADAS): boolean {
  const [esLider, setEsLider] = useState(false);
  useEffect(() => {
    if (!activo) return;
    const lider = crearLiderUnico({ clave: `${clave}:${globalThis.location?.host ?? ''}`, bloqueos: bloqueosDelNavegador(), alCambio: setEsLider });
    return seguirVisibilidad(lider, document);
  }, [activo, clave]);
  return esLider;
}

/**
 * Igual, pero SIN seguir la visibilidad: la pestaña conserva el bloqueo aunque esté oculta (una sola pestaña lo tiene a la vez). Es el respaldo del aviso
 * con sonido: si ninguna pestaña está visible, la que tiene este bloqueo es la que avisa (ver AvisoTelemedicina).
 */
export function useLiderDeSegundoPlano(activo: boolean, clave: string): boolean {
  const [esLider, setEsLider] = useState(false);
  useEffect(() => {
    if (!activo) return;
    const lider = crearLiderUnico({ clave: `${clave}:${globalThis.location?.host ?? ''}`, bloqueos: bloqueosDelNavegador(), alCambio: setEsLider });
    lider.iniciar();
    return () => lider.detener();
  }, [activo, clave]);
  return esLider;
}

/** Nombre completo del bloqueo (con el host) de una clave, tal como lo piden los hooks de arriba. */
export const nombreDeBloqueo = (clave: string): string => `${clave}:${globalThis.location?.host ?? ''}`;

/** ¿Hay alguna pestaña (visible) sosteniendo ese bloqueo AHORA? Sin `query` (navegador sin Web Locks) no se puede saber: se responde `true` (ninguna pestaña de respaldo avisa). */
export async function bloqueoEnUso(bloqueos: AdministradorBloqueos | null, nombre: string): Promise<boolean> {
  if (!bloqueos?.query) return true;
  try {
    const estado = await bloqueos.query();
    return (estado.held ?? []).some((b) => b.name === nombre);
  } catch {
    return true;
  }
}

